import * as action_utils from '@adornis/bh-util/action.js';
import type { BHTranslationDictionary } from '@adornis/buchungsheld-db/translations/translation.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import type { ChemistryControllerHost } from '@adornis/chemistry/controllers/chemistry-controller.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { getCurrentComponent, stateful, useEffect, useObservable } from '@adornis/functional-lit/stateful.js';
import { globalLanguageSubject } from '@adornis/translation-core/client/languageSubject.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { Signal } from '@lit-labs/preact-signals';
import { PartType } from 'lit/async-directive.js';
import { state } from 'lit/decorators.js';

export type BuchungsheldLitElementHost = ChemistryControllerHost & {
  _translation: TranslationController<BHTranslationDictionary>;
};

export const institutionPageActionConfig = new Signal<Array<Action>>([]);

export type Action = action_utils.Action;
export const renderAction = action_utils.renderAction;

const _actionProvider = new Map<any, Record<string, Action>>();
const notifyActionChange = () => {
  const allCurrentActions = [] as Array<Action>;
  for (const compActions of _actionProvider.values()) allCurrentActions.push(...Object.values(compActions));
  institutionPageActionConfig.value = allCurrentActions;
};

export const handleError = (translate, err) => {
  console.error(err);
  const errMessage = (() => {
    if (err?.extensions?.translationKey) {
      return translate('errors', err.extensions.translationKey);
    }

    const errCode = err?.message ?? 'unknown';
    return translate('err', errCode);
  })();

  return XSnackbar.show(errMessage);
};

// for use functional lit
export const useExposeActions = () => {
  const host = (() => {
    const component = getCurrentComponent();
    const part = component.instance.part;
    if (!part) return undefined;
    try {
      if (part.type !== PartType.CHILD) throw new Error('partType is not supported: ' + part.type);
      return (part as any).parentNode;
    } catch (e) {
      console.error(e);
      return undefined;
    }
  })();

  // createHook
  useEffect(() => {
    const cleanup = () => {
      _actionProvider.delete(host);
      notifyActionChange(); // notify removal of action
    };

    return cleanup;
  }, []);

  return {
    expose: (key: string, action: Action) => {
      if (!host) return;
      const componentActions =
        _actionProvider.get(host) ??
        (() => {
          const actions = {} as Record<string, Action>;
          _actionProvider.set(host, actions);
          return actions;
        })();
      componentActions[key] = action;

      notifyActionChange(); // notify new action

      // FIXME: this should not be necessary because of the cleanup hook
      function findRoots(ele) {
        return [ele, ...ele.querySelectorAll('*')]
          .filter(e => !!e.shadowRoot)
          .flatMap(e => [e.shadowRoot, ...findRoots(e.shadowRoot)]);
      }
      const shadowRoots = findRoots(document.body);
      for (const [host, actions] of _actionProvider.entries()) {
        let existsInShadow = false;
        for (const shadowRoot of shadowRoots) {
          if (shadowRoot.contains(host)) {
            existsInShadow = true;
            break;
          }
        }

        if (!document.body.contains(host) && !existsInShadow) {
          console.warn('found orphaned action provider; removing it now', { host, actions });
          _actionProvider.delete(host);
        }
      }
    },
  };
};

export function getExposedActionsOfElement(host: any) {
  return _actionProvider.get(host) ?? {};
}

export class BuchungsheldLitElement extends ChemistryLitElement {
  @state() readonly _translation = new TranslationController<any>(this);

  e = (err: any) => handleError(this._translation.translate, err);

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    _actionProvider.delete(this);
    notifyActionChange();
  }

  exposeAction(key: string, action: Action) {
    const componentActions =
      _actionProvider.get(this) ??
      (() => {
        const actions = {} as Record<string, Action>;
        _actionProvider.set(this, actions);
        return actions;
      })();

    // FIXME: this should not be necessary
    // clenup orphans
    for (const [host, actions] of _actionProvider.entries()) {
      if (host.isConnected) continue;
      console.warn('found orphaned action provider; removing it now', { host, actions });
      _actionProvider.delete(host);
    }

    // notify new action
    componentActions[key] = action;
    notifyActionChange();
  }
}

// translation
export const trn = stateful<[string, string]>(translation => {
  const language = useObservable(() => globalLanguageSubject, [42]);
  if (!language) return translation[0];
  return translation[language === 'de' ? 0 : 1];
});
